import React, { useEffect, useRef, useState, useCallback } from 'react';
import gsap from 'gsap';
import { X, ShoppingBag, Trash2, Plus, Minus, ArrowRight, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL, getImageUrl, getCart } from '../../apiConfig';
import './CartDrawer.css';

const CartDrawer = ({ isOpen, onClose }) => {
  const overlayRef = useRef(null);
  const drawerRef = useRef(null);
  const [cartItems, setCartItems] = useState([]);
  const [cartSummary, setCartSummary] = useState({ subtotal: 0, totalItems: 0, totalQty: 0 });
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();

  /* ── Normalize price from any format ── */
  const parsePrice = (price) => {
    if (!price) return 0;
    if (typeof price === 'number') return price;
    return parseInt(String(price).replace(/[^0-9]/g, '')) || 0;
  };

  /* ── Normalize image ── */
  const getImage = (item) => {
    if (item.imageSnapshot) return getImageUrl(item.imageSnapshot);
    const p = item.product || item; // fallback
    if (p.images && p.images.length > 0) return getImageUrl(p.images[0]);
    if (p.img) return getImageUrl(p.img);
    return 'https://via.placeholder.com/90x100?text=Fragrance';
  };

  /* ── Get Active Variant ID ── */
  const getActiveVariantId = (item) => {
    if (item.variantId) return item.variantId;
    if (item.variant && typeof item.variant === 'string') return item.variant;
    if (item.variant && typeof item.variant === 'object') return item.variant._id || item.variant.id;

    const p = item.product || item;
    const actualProductId = item.productId || item.product_id || (item.product && (item.product._id || item.product.id)) || (typeof item.product === 'string' ? item.product : null) || p._id || p.id;
    
    try {
      const userVariants = JSON.parse(localStorage.getItem('user_selected_variants') || '{}');
      if (actualProductId && userVariants[actualProductId]) return userVariants[actualProductId];
    } catch(e) {}

    return null;
  };

  /* ── Normalize price ── */
  const getPrice = (item) => {
    if (item.priceSnapshot && typeof item.priceSnapshot === 'number') return item.priceSnapshot;
    if (item.price && typeof item.price === 'number') return item.price;
    const p = item.product || item;
    
    const variantId = getActiveVariantId(item);
    if (variantId && p.variants && p.variants.length > 0) {
      const matched = p.variants.find(v => (v._id || v.id) === variantId);
      if (matched) return parsePrice(matched.finalPrice || matched.price);
    }

    if (item.variant && typeof item.variant === 'object') {
      const v = item.variant;
      if (v.finalPrice || v.price) return parsePrice(v.finalPrice || v.price);
    }
    
    const rawPrice = p.variants?.[0]?.finalPrice || p.variants?.[0]?.price || p.price || 0;
    
    const vol = getVolume(item).toLowerCase();
    let multiplier = 1;
    if (vol.includes('50ml') && (!p.variants || p.variants.length === 0)) multiplier = 0.6;
    if (vol.includes('200ml') && (!p.variants || p.variants.length === 0)) multiplier = 1.8;
    
    return Math.round(parsePrice(rawPrice) * multiplier);
  };

  /* ── Normalize volume ── */
  const getVolume = (item) => {
    const p = item.product || item;
    const actualProductId = item.productId || item.product_id || (item.product && (item.product._id || item.product.id)) || (typeof item.product === 'string' ? item.product : null) || p._id || p.id;
    
    const variantId = getActiveVariantId(item);

    // 1. HIGHEST PRIORITY: Local Storage (What user actually clicked)
    try {
      const userVols = JSON.parse(localStorage.getItem('user_selected_volumes') || '{}');
      if (actualProductId && variantId && userVols[`${actualProductId}_${variantId}`]) {
        return userVols[`${actualProductId}_${variantId}`];
      }
      if (actualProductId && userVols[actualProductId]) return userVols[actualProductId];
    } catch(e) {}

    // 2. MATCH VIA LOCALLY SAVED OR BACKEND VARIANT ID
    if (variantId && typeof variantId === 'string' && p.variants && p.variants.length > 0) {
      const matched = p.variants.find(v => (v._id || v.id) === variantId);
      if (matched) return matched.size || matched.volume || matched.name || matched.label || '100 ML';
    }
    
    // 3. BACKEND VARIANT OBJECT POPULATED
    if (item.variant && typeof item.variant === 'object') {
      const v = item.variant;
      const label = v.size || v.volume || v.name || v.label;
      if (label) return label;
    }

    // 4. BACKEND ITEM EXPLICIT VOLUME
    if (item.volume && typeof item.volume === 'string') return item.volume;
    if (item.size && typeof item.size === 'string') return item.size;

    // 5. FALLBACK
    return p.variants?.[0]?.size || p.variants?.[0]?.volume || p.variants?.[0]?.name || p.volume || '100 ML';
  };

  /* ── Normalize brand ── */
  const getBrand = (item) => {
    const p = item.product || item;
    return p.brand || p.collection || 'ESCENTRUM';
  };

  /* ── Normalize name ── */
  const getName = (item) => {
    if (item.titleSnapshot) return item.titleSnapshot;
    const p = item.product || item;
    return p.name || 'Unknown Product';
  };

  /* ── Merge duplicate cart items by product/variant ID ── */
  const mergeCartItems = (items) => {
    if (!Array.isArray(items)) return [];
    const map = new Map();
    items.forEach(item => {
      const pId = item.product?._id || item.product?.id || item._id || item.id;
      const vId = item.variantId || item.product?.variants?.[0]?._id || item.product?.variants?.[0]?.id || 'default';
      const key = `${pId}-${vId}`;
      const itemQty = item.qty !== undefined ? item.qty : (item.quantity || 1);

      if (map.has(key)) {
        const existing = map.get(key);
        const newQty = existing.qty + itemQty; existing.qty = newQty; existing.quantity = newQty;
        if (!existing.allIds) existing.allIds = [existing._id || existing.id];
        if (item._id || item.id) existing.allIds.push(item._id || item.id);
      } else {
        const newItem = { ...item, qty: itemQty, quantity: itemQty };
        newItem.allIds = [item._id || item.id];
        map.set(key, newItem);
      }
    });
    return Array.from(map.values());
  };

  /* ── Load cart from API ── */
  const loadCart = useCallback(async () => {
    const cartData = await getCart();
    console.log('[CartDrawer] API Response:', cartData); // DEBUG
    console.log('[CartDrawer] Items:', cartData.items);   // DEBUG
    setCartItems(mergeCartItems(cartData.items || []));
    setCartSummary({
      subtotal: cartData.subtotal || 0,
      totalItems: cartData.totalItems || 0,
      totalQty: cartData.totalQty || 0,
    });
  }, []);

  const handleCartUpdated = useCallback((e) => {
    // If the update originated from CartDrawer itself, do NOT reload from API!
    // This ensures user's optimistic updates/deletions are preserved exactly as intended
    // until they make another change or reload the page.
    if (e?.detail?.source === 'CartDrawer') {
      return;
    }
    loadCart();
  }, [loadCart]);

  useEffect(() => {
    loadCart();
    window.addEventListener('cartUpdated', handleCartUpdated);
    return () => window.removeEventListener('cartUpdated', handleCartUpdated);
  }, [loadCart, handleCartUpdated]);

  /* ── Update quantity via API (Optimistic UI) ── */
  const updateQty = async (index, delta) => {
    const item = cartItems[index];
    const currentQty = item.qty !== undefined ? item.qty : (item.quantity || 1);
    const newQty = currentQty + delta;
    const token = localStorage.getItem('token');
    const itemId = item._id || item.id;

    console.log(`[CartDrawer] updateQty called: index=${index}, delta=${delta}, currentQty=${currentQty}, newQty=${newQty}, itemId=${itemId}`);

    if (newQty < 1) {
      removeItem(index);
      return;
    }

    // ⚡ OPTIMISTIC UPDATE: Fauran UI update karo!
    const updatedItems = [...cartItems];
    updatedItems[index] = { ...updatedItems[index], qty: newQty, quantity: newQty };
    setCartItems(updatedItems);

    const newSubtotal = updatedItems.reduce((sum, it) => sum + (getPrice(it) * (it.qty !== undefined ? it.qty : (it.quantity || 1))), 0);
    const newTotalQty = updatedItems.reduce((sum, it) => sum + (it.qty !== undefined ? it.qty : (it.quantity || 1)), 0);
    setCartSummary({ subtotal: newSubtotal, totalItems: updatedItems.length, totalQty: newTotalQty });

    // Instantly update Navbar digits and inform app, but pass source: 'CartDrawer'
    window.dispatchEvent(new CustomEvent('cartUpdated', { 
      detail: { totalQty: newTotalQty, totalItems: updatedItems.length, source: 'CartDrawer' } 
    }));

    // Background API Call
    if (token) {
      try {
        let response = await fetch(`${BASE_URL}Cart/items/${itemId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ qty: newQty, quantity: newQty })
        });
        if (!response.ok) {
          response = await fetch(`${BASE_URL}Cart/items/${itemId}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ qty: newQty, quantity: newQty })
          });
        }
        console.log(`[CartDrawer] PUT/PATCH item response status:`, response.status);
      } catch (e) { console.error("[CartDrawer] PUT/PATCH Catch Error:", e); }
    } else {
      const localCart = JSON.parse(localStorage.getItem('localCart') || '[]');
      const localItem = localCart.find(i => i._id === itemId || i.id === itemId);
      if (localItem) {
        localItem.qty = newQty;
        localItem.quantity = newQty;
        localStorage.setItem('localCart', JSON.stringify(localCart));
      }
    }
  };

  /* ── Remove item via API (Optimistic UI) ── */
  const removeItem = async (index) => {
    const item = cartItems[index];
    const itemId = item._id || item.id;
    const token = localStorage.getItem('token');

    console.log(`[CartDrawer] removeItem called: index=${index}, itemId=${itemId}`);

    // ⚡ OPTIMISTIC UPDATE: Fauran UI se filter out karo!
    const updatedItems = cartItems.filter((_, i) => i !== index);
    setCartItems(updatedItems);

    const newSubtotal = updatedItems.reduce((sum, it) => sum + (getPrice(it) * (it.qty !== undefined ? it.qty : (it.quantity || 1))), 0);
    const newTotalQty = updatedItems.reduce((sum, it) => sum + (it.qty !== undefined ? it.qty : (it.quantity || 1)), 0);
    setCartSummary({ subtotal: newSubtotal, totalItems: updatedItems.length, totalQty: newTotalQty });

    // Instantly update Navbar digits and inform app, but pass source: 'CartDrawer'
    window.dispatchEvent(new CustomEvent('cartUpdated', { 
      detail: { totalQty: newTotalQty, totalItems: updatedItems.length, source: 'CartDrawer' } 
    }));

    if (token) {
      try {
        const idsToDelete = item.allIds || [itemId];
        for (const id of idsToDelete) {
          if (id) {
            await fetch(`${BASE_URL}Cart/items/${id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${token}` }
            });
          }
        }
        console.log(`[CartDrawer] DELETED all duplicate item IDs successfully`);
      } catch (e) { console.error("[CartDrawer] DELETE Catch Error:", e); }
    } else {
      const localCart = JSON.parse(localStorage.getItem('localCart') || '[]');
      const filtered = localCart.filter(i => i._id !== itemId && i.id !== itemId);
      localStorage.setItem('localCart', JSON.stringify(filtered));
    }
  };

  /* ── Clear entire cart via API (Optimistic UI) ── */
  const clearCart = async () => {
    const token = localStorage.getItem('token');
    
    // Save items to clear in background API calls
    const itemsToClear = [...cartItems];

    // ⚡ OPTIMISTIC UPDATE: Clear UI immediately
    setCartItems([]);
    setCartSummary({ subtotal: 0, totalItems: 0, totalQty: 0 });

    window.dispatchEvent(new CustomEvent('cartUpdated', { 
      detail: { totalQty: 0, totalItems: 0, source: 'CartDrawer' } 
    }));

    // Background API Clear Calls
    if (token) {
      try {
        const deletePromises = [];
        
        // 1. Delete all items individually to guarantee persistence (100% bulletproof)
        itemsToClear.forEach(item => {
          const idsToDelete = item.allIds || [item._id || item.id];
          idsToDelete.forEach(id => {
            if (id) {
              deletePromises.push(
                fetch(`${BASE_URL}Cart/items/${id}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` }
                }).catch(err => console.error(`Error deleting item ${id}:`, err))
              );
            }
          });
        });

        // 2. Also try root Cart DELETE just in case it is supported by the API
        deletePromises.push(
          fetch(`${BASE_URL}Cart`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          }).catch(err => console.error("Error clearing root cart:", err))
        );

        // Run all requests in parallel for maximum performance
        await Promise.all(deletePromises);
        console.log(`[CartDrawer] Clear Cart complete: All items deleted successfully`);
      } catch (e) { 
        console.error("[CartDrawer] Clear Catch Error:", e); 
      }
    } else {
      localStorage.setItem('localCart', JSON.stringify([]));
    }
  };

  /* 🔒 Proceed to Checkout 🔒 */
  const handleProceedToCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  /* ── Mount / Unmount with animation ── */
  useEffect(() => {
    if (isOpen) setMounted(true);
  }, [isOpen]);

  useEffect(() => {
    if (!mounted) return;
    if (isOpen) {
      gsap.set(overlayRef.current, { opacity: 0 });
      gsap.set(drawerRef.current, { x: '100%' });
      const tl = gsap.timeline();
      tl.to(overlayRef.current, { opacity: 1, duration: 0.4, ease: 'power2.out' })
        .to(drawerRef.current, { x: '0%', duration: 0.6, ease: 'expo.out' }, '-=0.2')
        .fromTo('.cd-header, .cd-item, .cd-summary, .cd-empty-state',
          { x: 40, opacity: 0 },
          { x: 0, opacity: 1, duration: 0.4, stagger: 0.07, ease: 'power2.out' },
          '-=0.3'
        );
      document.body.style.overflow = 'hidden';
    } else {
      const tl = gsap.timeline({ onComplete: () => setMounted(false) });
      tl.to(drawerRef.current, { x: '100%', duration: 0.4, ease: 'power2.in' })
        .to(overlayRef.current, { opacity: 0, duration: 0.3 }, '-=0.2');
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, mounted]);

  // Use API-provided totals
  const total = cartSummary.subtotal;
  const totalItems = cartSummary.totalQty || cartItems.length;

  if (!mounted) return null;

  return (
    <div className="cd-root">
      {/* Overlay */}
      <div className="cd-overlay" ref={overlayRef} onClick={onClose} />

      {/* Drawer */}
      <div className="cd-drawer" ref={drawerRef}>

        {/* Header */}
        <div className="cd-header">
          <div className="cd-header-left">
            <div className="cd-header-icon">
              <ShoppingBag size={18} />
            </div>
            <div>
              <h3 className="cd-title">YOUR BAG</h3>
              <p className="cd-subtitle">
                {totalItems} {totalItems === 1 ? 'item' : 'items'} · PKR {total.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="cd-header-actions">
            {cartItems.length > 0 && (
              <button className="cd-clear-btn" onClick={clearCart} title="Clear all">
                <Trash2 size={14} />
              </button>
            )}
            <button className="cd-close-btn" onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="cd-divider" />

        {/* Content */}
        <div className="cd-body">
          {cartItems.length === 0 ? (
            <div className="cd-empty-state">
              <div className="cd-empty-icon">
                <Package size={48} strokeWidth={1} />
              </div>
              <h4>Your bag is empty</h4>
              <p>Add your favourite fragrances to begin.</p>
              <button className="cd-explore-btn" onClick={() => {
                onClose();
                navigate('/shop');
              }}>
                EXPLORE COLLECTION
                <ArrowRight size={14} />
              </button>
            </div>
          ) : (
            <div className="cd-items-list">
              {cartItems.map((item, idx) => {
                const price = getPrice(item);
                const qty = item.qty !== undefined ? item.qty : (item.quantity || 1);
                const img = getImage(item);
                const brand = getBrand(item);
                const volume = getVolume(item);
                const name = getName(item);
                return (
                  <div key={item._id || item.id || idx} className="cd-item">
                    <div className="cd-item-img">
                      <img
                        src={img}
                        alt={name}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/90x100?text=Fragrance';
                        }}
                      />
                    </div>
                    <div className="cd-item-info">
                      <span className="cd-item-brand">{brand}</span>
                      <h4 className="cd-item-name">{name}</h4>
                      <p className="cd-item-vol">{volume}</p>
                      <div className="cd-item-bottom">
                        <div className="cd-qty-ctrl">
                          <button onClick={() => updateQty(idx, -1)}><Minus size={10} /></button>
                          <span>{qty}</span>
                          <button onClick={() => updateQty(idx, 1)}><Plus size={10} /></button>
                        </div>
                        <span className="cd-item-price">
                          PKR {(price * qty).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <button className="cd-remove-btn" onClick={() => removeItem(idx)}>
                      <X size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Summary Footer */}
        {cartItems.length > 0 && (
          <div className="cd-summary">
            <div className="cd-summary-gradient" />
            <div className="cd-summary-row">
              <span>{totalItems} {totalItems === 1 ? 'item' : 'items'}</span>
              <span>PKR {total.toLocaleString()}</span>
            </div>
            <div className="cd-summary-row cd-summary-total">
              <span>Total</span>
              <span className="cd-total-val">PKR {total.toLocaleString()}</span>
            </div>
            <p className="cd-summary-note">Taxes & shipping calculated at checkout</p>
            <button className="cd-checkout-btn" onClick={handleProceedToCheckout}>
              <span>PROCEED TO CHECKOUT</span>
              <div className="cd-btn-arrow"><ArrowRight size={16} /></div>
            </button>
            <button className="cd-continue-btn" onClick={onClose}>
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
